import {
    ATS_CV_MARGIN_BOTTOM,
    ATS_CV_MARGIN_TOP,
    ATS_CV_PAGE_MIN_HEIGHT,
} from "./atsCvLayout.js";
import { buildAtsCvData } from "./atsCvFormEngine.js";

/** Usable content height inside one A4 sheet (px). */
export const ATS_CV_PAGE_CONTENT_HEIGHT =
    ATS_CV_PAGE_MIN_HEIGHT - ATS_CV_MARGIN_TOP - ATS_CV_MARGIN_BOTTOM;

/** Extra px between stacked blocks on a page (margins not in offsetHeight). */
export const ATS_CV_BLOCK_GAP = 10;

/** Extra px reserved above the bottom page margin when packing blocks. */
export const ATS_CV_PAGE_PACK_BUFFER = 36;

/** Small safety margin per measured block to avoid sub-pixel overflow clips. */
export const ATS_CV_BLOCK_MEASURE_SAFETY = 2;

/**
 * Flat blocks for pagination (measure → pack into pages).
 * @param {import("./atsCvFormEngine.js").INITIAL_ATS_CV_FORM} form
 * @param {string} [template] the sidebar and split templates keep its summary, skills, certifications and
 *   languages in the rail, so only the paginated main column is built here
 * @returns {{ id: string, kind: string, sectionTitle?: string, heightHint?: number }[]}
 */
export function buildAtsCvPreviewBlockMeta(form, template) {
    const d = buildAtsCvData(form);
    const sidebar = template === "sidebar" || template === "split" || template === "bold";
    const t = d.sectionTitles;
    const blocks = [];

    const showHeader =
        d.fullName ||
        d.jobTitle ||
        d.email ||
        d.phone ||
        d.location ||
        d.linkedin ||
        d.website;

    if (showHeader) {
        blocks.push({ id: "header", kind: "header" });
    }

    if (d.summary && !sidebar) {
        blocks.push({
            id: "summary-title",
            kind: "section-title",
            sectionTitle: t.summary,
        });
        blocks.push({ id: "summary-body", kind: "section-body", sectionTitle: t.summary });
    }

    if (d.experiences.length) {
        blocks.push({
            id: "experience-title",
            kind: "section-title",
            sectionTitle: t.experience,
        });
        d.experiences.forEach((_, i) => {
            blocks.push({
                id: `experience-${i}`,
                kind: "entry",
                sectionTitle: t.experience,
            });
        });
    }

    if (d.education.length) {
        blocks.push({
            id: "education-title",
            kind: "section-title",
            sectionTitle: t.education,
        });
        d.education.forEach((_, i) => {
            blocks.push({
                id: `education-${i}`,
                kind: "entry",
                sectionTitle: t.education,
            });
        });
    }

    if (d.projects.length) {
        blocks.push({ id: "projects-title", kind: "section-title", sectionTitle: t.projects });
        d.projects.forEach((_, i) => {
            blocks.push({
                id: `project-${i}`,
                kind: "entry",
                sectionTitle: t.projects,
            });
        });
    }

    if (d.skills.length && !sidebar) {
        blocks.push({
            id: "skills-title",
            kind: "section-title",
            sectionTitle: t.skills,
        });
        blocks.push({ id: "skills-body", kind: "section-body", sectionTitle: t.skills });
    }

    if (d.certifications.length && !sidebar) {
        blocks.push({
            id: "certifications-title",
            kind: "section-title",
            sectionTitle: t.certifications,
        });
        blocks.push({
            id: "certifications-body",
            kind: "section-body",
            sectionTitle: t.certifications,
        });
    }

    if (d.languages.length && !sidebar) {
        blocks.push({
            id: "languages-title",
            kind: "section-title",
            sectionTitle: t.languages,
        });
        blocks.push({ id: "languages-body", kind: "section-body", sectionTitle: t.languages });
    }

    d.customSections.forEach((sec) => {
        blocks.push({
            id: `custom-${sec.id}-title`,
            kind: "section-title",
            sectionTitle: sec.title,
        });
        blocks.push({
            id: `custom-${sec.id}-body`,
            kind: "section-body",
            sectionTitle: sec.title,
        });
    });

    return { blocks, data: d, titles: t };
}

/**
 * @param {{ kind?: string }[]} blocks
 * @param {number} index
 * @returns {{ ids: string[], endIndex: number }}
 */
function getPackGroup(blocks, index) {
    const block = blocks[index];
    const next = blocks[index + 1];

    if (block.kind === "section-title" && next?.kind === "section-body") {
        return { ids: [block.id, next.id], endIndex: index + 1 };
    }

    /* Keep heading with first entry — avoids orphan title at page bottom */
    if (
        block.kind === "section-title" &&
        next?.kind === "entry" &&
        next.sectionTitle === block.sectionTitle
    ) {
        return { ids: [block.id, next.id], endIndex: index + 1 };
    }

    return { ids: [block.id], endIndex: index };
}

/**
 * @param {{ id: string }[]} blocks
 * @param {number[]} heights
 * @param {number} pageHeight
 * @returns {string[][]} block ids per page
 */
export function packBlocksIntoPages(
    blocks,
    heights,
    pageHeight = ATS_CV_PAGE_CONTENT_HEIGHT
) {
    if (!blocks.length) return [];

    const maxH = pageHeight - ATS_CV_PAGE_PACK_BUFFER;
    const pages = [];
    let current = [];
    let used = 0;

    const groupHeight = (start, end) => {
        let h = 0;
        for (let j = start; j <= end; j++) {
            h += heights[j] || 40;
            if (j > start) h += ATS_CV_BLOCK_GAP;
        }
        return h;
    };

    const flush = () => {
        if (!current.length) return;
        pages.push(current);
        current = [];
        used = 0;
    };

    for (let i = 0; i < blocks.length; ) {
        const packGroup = getPackGroup(blocks, i);
        let groupEnd = packGroup.endIndex;
        let groupIds = packGroup.ids;
        let h = groupHeight(i, groupEnd);

        const gap = current.length > 0 ? ATS_CV_BLOCK_GAP : 0;
        const remaining = maxH - used - gap;

        if (current.length > 0 && h > remaining) {
            flush();
            continue;
        }

        used += gap + h;
        groupIds.forEach((id) => current.push(id));
        i = groupEnd + 1;
    }

    flush();

    return pages.length ? pages : [blocks.map((b) => b.id)];
}

/**
 * @param {HTMLElement | null} el
 */
export function measurePreviewBlockHeight(el) {
    if (!el) return 48;
    const style = window.getComputedStyle(el);
    const mt = parseFloat(style.marginTop) || 0;
    const mb = parseFloat(style.marginBottom) || 0;
    // offsetHeight, NOT the bounding rect: the preview is painted inside a
    // `transform: scale()` canvas, and a rect measured under one comes back in
    // screen pixels. Every block would then measure a fraction of its real
    // height, the packer would pour far too much onto page one, and the
    // overflow would be clipped off the bottom of the sheet — content simply
    // gone. offsetHeight is layout pixels, which is what the page is packed in.
    const height = el.offsetHeight || el.getBoundingClientRect().height;
    return Math.ceil(height + mt + mb + ATS_CV_BLOCK_MEASURE_SAFETY) || 48;
}
